/**
 * The alerts screens' pure decisions (phase 7b item 4): how a severity shows,
 * the badge's count and tone, a type's label, and the settings form's
 * dirtiness. Table-tested in `alerts.test.ts`.
 */
import type { AlertCounts, AlertSettings, UpdateAlertSettingsRequest } from '@acp/api-contract/alerts.js';
import { ALERT_CATALOG, type AlertType, type Severity } from '@acp/tenant-alerts/catalog.js';

export type SeverityTone = 'offline' | 'warning' | 'secondary';

/** A severity's badge variant: critical is the `offline` red, warning the amber, info plain. */
export function severityTone(severity: Severity): SeverityTone {
  switch (severity) {
    case 'critical':
      return 'offline';
    case 'warning':
      return 'warning';
    case 'info':
      return 'secondary';
  }
}

export function alertLabel(type: AlertType): string {
  return ALERT_CATALOG[type].label;
}

/** What the top bar's badge shows: how many active alerts, and the worst severity among them (null: none). */
export function badgeOf(counts: AlertCounts): { readonly total: number; readonly worst: Severity | null } {
  const total = counts.critical + counts.warning + counts.info;
  const worst: Severity | null = counts.critical > 0 ? 'critical' : counts.warning > 0 ? 'warning' : counts.info > 0 ? 'info' : null;
  return { total, worst };
}

/** The part of the settings the form edits (the channels have their own controls). */
export type RulesDraft = Pick<UpdateAlertSettingsRequest, 'cooldownMinutes' | 'rules' | 'siteOverrides'>;

export function draftOf(settings: AlertSettings): RulesDraft {
  return { cooldownMinutes: settings.cooldownMinutes, rules: settings.rules, siteOverrides: settings.siteOverrides };
}

/** Whether the draft differs from what the settings say (the live-update banner's question, D35). */
export function isRulesDirty(draft: RulesDraft, base: AlertSettings): boolean {
  return JSON.stringify(draft) !== JSON.stringify(draftOf(base));
}
